import { useCallback, useMemo, useRef, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { createNote, updateNote } from '../../../services/notes.js';

/**
 * Create or update a note (CT-5528).
 *
 * Mirrors the shape of Governance's `useCreateTruthPlaneForm` — a `submittingRef`
 * guard, toasts as `{ title, message }`, a best-effort refresh once it worked —
 * without importing from it: apps never depend on each other.
 *
 * `save` resolves to the saved note's id, or `null` on failure. On failure it
 * toasts and DOES NOT close or reset anything: the caller keeps every value the
 * user typed. A double click saves once — the ref flips before any state
 * update lands, so the second click is already refused.
 *
 * @param {object} options
 * @param {string} options.entityType
 * @param {string} options.entityId
 * @param {() => void} [options.onSaved] — best-effort refresh (the list, quietly).
 */
export function useSaveNote({ entityType, entityId, onSaved } = {}) {
  const { t } = useTranslation('unified-profiles');
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);

  const save = useCallback(
    async ({ noteId, title, body, scope, attachments, urls }) => {
      if (submittingRef.current) return null;
      submittingRef.current = true;
      setSaving(true);

      try {
        const isEdit = Boolean(noteId);
        const { body: result } = isEdit
          ? await updateNote({ noteId, title, body, scope, attachments, urls })
          : await createNote({ entityType, entityId, title, body, scope, attachments, urls });

        toast.success({
          title: t(isEdit ? 'notes.toast.updatedTitle' : 'notes.toast.createdTitle'),
          message: t(isEdit ? 'notes.toast.updatedMessage' : 'notes.toast.createdMessage'),
        });
        // Best effort: the note is saved either way, a failed refresh must not undo that.
        try {
          onSaved?.();
        } catch {
          /* the list will refresh the next time it loads */
        }
        return result?.note_id ?? noteId ?? null;
      } catch (err) {
        toast.error({
          title: t('notes.toast.saveErrorTitle'),
          message: err?.message ?? t('notes.toast.saveErrorMessage'),
        });
        return null;
      } finally {
        submittingRef.current = false;
        setSaving(false);
      }
    },
    [entityType, entityId, onSaved, toast, t],
  );

  return useMemo(() => ({ save, saving }), [save, saving]);
}

export default useSaveNote;
